import type { FilterOption } from "@workspace/ui/components";
import type { FilterData, SearchFilters } from "@workspace/ui/types";

import { CategoryAPI, ProductSearchAPI } from "@/const/endPoint";

// Funkcje pomocnicze do konwersji - zachowuje polskie znaki
export const labelToSlug = (label: string): string => {
  return label.toLowerCase().replace(/\s+/g, "-");
};

export const slugToLabel = (slug: string): string => {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Funkcje do obsługi URL params
export const updateUrlParams = (
  searchParams: URLSearchParams,
  filteredColorOptions: FilterOption[],
  filteredSizeOptions: FilterOption[],
) => {
  const url = new URL(window.location.href);

  // Usuń stare parametry
  url.searchParams.delete("kolor");
  url.searchParams.delete("rozmiar");

  // Dodaj kolory
  if (filteredColorOptions.length > 0) {
    const colorSlugs = filteredColorOptions.map((option) =>
      labelToSlug(option.label),
    );
    url.searchParams.set("kolor", colorSlugs.join(","));
  }

  // Dodaj rozmiary
  if (filteredSizeOptions.length > 0) {
    const sizeSlugs = filteredSizeOptions.map((option) =>
      labelToSlug(option.label),
    );
    url.searchParams.set("rozmiar", sizeSlugs.join(","));
  }

  // Aktualizuj URL bez przeładowania strony
  window.history.replaceState({}, "", url.toString());
};

export const parseUrlParams = (
  searchParams: URLSearchParams,
  filterData: FilterData,
): {
  colors: FilterOption[];
  sizes: FilterOption[];
  features: FilterOption[];
} => {
  const colors: FilterOption[] = [];
  const sizes: FilterOption[] = [];
  const features: FilterOption[] = [];

  // Parsuj kolory z URL
  const colorParam = searchParams.get("kolor");
  if (colorParam && filterData.colors) {
    const colorSlugs = colorParam.split(",");
    colorSlugs.forEach((slug) => {
      const matchedColor = filterData.colors.find(
        (color) => labelToSlug(color.label) === slug,
      );
      if (matchedColor) {
        colors.push(matchedColor);
      }
    });
  }

  // Parsuj rozmiary z URL
  const sizeParam = searchParams.get("rozmiar");
  if (sizeParam && filterData.sizes) {
    const sizeSlugs = sizeParam.split(",");
    sizeSlugs.forEach((slug) => {
      const matchedSize = filterData.sizes.find(
        (size) => labelToSlug(size.label) === slug,
      );
      if (matchedSize) {
        sizes.push(matchedSize);
      }
    });
  }

  // Parsuj cechy z URL
  const featureParam = searchParams.get("cecha");
  if (featureParam && filterData.features) {
    const featureSlugs = featureParam.split(",");
    featureSlugs.forEach((slug) => {
      const matchedFeature = filterData.features.find(
        (feature) => labelToSlug(feature.label) === slug,
      );
      if (matchedFeature) {
        features.push(matchedFeature);
      }
    });
  }

  return { colors, sizes, features };
};

export const buildSearchUrl = (
  searchQuery: string,
  page: number,
  category: "odziez-damska" | "odziez-meska" | "odziez-dziecieca" | "",
  filters: SearchFilters,
  type: "category" | "search",
) => {
  const params = new URLSearchParams();

  if (type === "search") {
    params.append("s", searchQuery);
  } else if (type === "category") {
    params.append("slug", searchQuery);
  }

  params.append("resultsPerPage", "12");
  params.append("page", page.toString());

  if (type === "search") {
    params.append("category", category);
  }

  if (filters.colors.length > 0) {
    params.append("colors", filters.colors.join(","));
  }

  if (filters.sizes.length > 0) {
    params.append("sizes", filters.sizes.join(","));
  }

  if (filters.features.length > 0) {
    params.append("features", filters.features.join(","));
  }

  if (filters.minPrice !== null) {
    params.append("min_price", filters.minPrice.toString());
  }

  if (filters.maxPrice !== null) {
    params.append("max_price", filters.maxPrice.toString());
  }

  if (filters.onSaleOnly) {
    params.append("on_sale", "1");
  }

  if (filters.sort && filters.sort !== "default") {
    params.append("sort", filters.sort);
  }

  if (type === "category") {
    params.append("with_all_images", "0");
    params.append("image_size", "home_default");
    params.append("with_breadcrumb", "true");
  }

  if (type === "category") {
    return `/rest${CategoryAPI}?${params.toString()}`;
  } else {
    return `/rest${ProductSearchAPI}?${params.toString()}`;
  }
};
